// ui/src/config/menu.ts
// Arquivo centralizado para gerenciar menus vertical e horizontal

import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import { moduleSlugForPath } from '@/config/moduleRegistry';
import { hasMenuAccess } from '@/utils/routePermissions';
import { isPermissionsConfigurationPath, normalizeRoutePath, resolveUserRouteAccessLevel } from '@/utils/userRouteAccess';

export interface MenuItem {
  header?: string;
  title?: string;
  subCaption?: string;
  icon?: string;
  BgColor?: string;
  to?: string;
  type?: 'external'; // Para links externos (abre em nova aba)
  children?: MenuItem[];
  isAllowed?: string[]; // grupos, departamentos ou empresas permitidos (legado)
  active?: boolean; // indica se o item está ativo (rota atual corresponde)
  exact?: boolean; // se true, só ativa com path exato (sem sub-rotas)
  /**
   * Badge dinâmico de notificação ao lado do título (ex.: 'ticketNew' = chamados novos).
   * 'reembolsoAprovacao'/'reembolsoConferencia' ficam nos itens folha (Minhas solicitações/
   * Conferência) — o NavCollapse borbulha o sino para os grupos pai (Reembolso/Financeiro).
   */
  badgeKey?: 'ticketNew' | 'purchaseOrderNew' | 'reembolsoAprovacao' | 'reembolsoConferencia';
  /**
   * Flag "New" de módulo recém-lançado: teto absoluto (ISO YYYY-MM-DD) — depois dessa
   * data o item NUNCA mais mostra "New", não importa o usuário. Dentro desse teto, a
   * etiqueta some por usuário 1 semana após o PRIMEIRO LOGIN dele (não é uma janela global
   * fixa — quem entra no Hub depois do lançamento ainda vê "New" por 1 semana inteira).
   * Ver memória feedback_new_module_flag.
   */
  newUntil?: string;
  /**
   * Quando true, o item só deve aparecer se o usuário for aprovador
   * de reembolso (flag dinâmica obtida por `useReembolsoAprovadorFlag`).
   */
  requiresAprovador?: boolean;
  /**
   * Quando true, o item só deve aparecer se o usuário for gestor de cartão
   * (flag dinâmica obtida por `useCartaoGestorFlag`).
   */
  requiresGestor?: boolean;
  /**
   * Resgate por papel dinâmico: mostra o item MESMO com nível 'none' no userAccessMap
   * quando a flag indicada em `authStore.roleFlags` for true. Usado para agentes do
   * Service Desk sem permissão de rota (ex.: prestador externo agente de entidade) —
   * o inverso de `requiresGestor`, que só ESCONDE.
   */
  roleFlagAccess?: 'isAprovadorReembolso' | 'isGestorCartao' | 'isAgenteDesk';
  /** Slug de módulo explícito — sobrepõe a detecção automática por rota (útil para itens cujo `to` não casa com o prefixo do módulo). */
  module?: string;
  /** Multi-tenant: item exclusivo do tenant master (Atlan) — ex.: administração de tenants. */
  masterOnly?: boolean;
  /**
   * Quando true, o item só deve aparecer se o usuário for conferente do
   * financeiro (flag dinâmica obtida por `useReembolsoConferenteFlag`).
   */
  requiresConferente?: boolean;
  /**
   * Central do financeiro: conferente OU admin da configuração do Reembolso (que vê a
   * Central mesmo fora do pool — decisão 4). Flag obtida por `useReembolsoCentralFlag`.
   */
  requiresCentralFinanceiro?: boolean;
  /**
   * Quando true, o item só deve aparecer se o usuário for avaliador de
   * inovação — diretor de ≥1 centro de custo no Identidade ou admin da rota
   * /inovacao (flag dinâmica obtida por `useInovacaoAvaliadorFlag`).
   */
  requiresAvaliadorInovacao?: boolean;
}

/**
 * Item de módulo recém-lançado ainda dentro da janela "New" (ver MenuItem.newUntil).
 * Duas condições: (1) dentro do teto absoluto `newUntil` do item; (2) dentro de 1 semana
 * a partir do PRIMEIRO LOGIN do usuário (`firstLoginAt` — vem de authStore.user). Sem
 * `firstLoginAt` disponível (estado degradado/fallback do JWT), não mostra a etiqueta.
 * Compara datas ISO (YYYY-MM-DD) lexicograficamente — precisão de dia basta; some sozinho.
 */
export function isMenuItemNew(item: MenuItem, firstLoginAt?: string | null): boolean {
  if (!item.newUntil || !firstLoginAt) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (today > item.newUntil) return false;
  const weekAfterFirstLogin = new Date(firstLoginAt);
  weekAfterFirstLogin.setDate(weekAfterFirstLogin.getDate() + 7);
  return today <= weekAfterFirstLogin.toISOString().slice(0, 10);
}

/**
 * Chave ÚNICA e estável de um grupo colapsável do menu lateral.
 * É HIERÁRQUICA (títulos dos ancestrais + o próprio, unidos por '/') para não
 * colidir quando dois grupos têm o mesmo título (ex.: "Relatórios" em Financeiro
 * e em Logística). Sem isso, abrir um abriria o outro (mesmo `v-list-group` value).
 */
export function menuGroupKey(title: string, parentKey?: string): string {
  return parentKey ? `${parentKey}/${title}` : title;
}

/** Chaves de grupos com filhos — usado para `v-model:opened` do menu lateral. */
export function collectCollapsibleMenuTitles(items: MenuItem[], parentKey = ''): string[] {
  const out: string[] = [];
  for (const it of items) {
    if (it.header) continue;
    if (it.children?.length && it.title) {
      const key = menuGroupKey(it.title, parentKey || undefined);
      out.push(key);
      out.push(...collectCollapsibleMenuTitles(it.children, key));
    }
  }
  return out;
}

const menuItemsBruto: MenuItem[] = [
  { header: 'Menu' },
  {
    title: 'Home',
    icon: 'home-2-line-duotone',
    BgColor: 'primary',
    to: '/',
  },
  {
    // Gaveta de placares executivos: painel de leitura em ~10s para a diretoria. Já teve posição
    // fixa logo depois da Home (decisão de 08/09/2026); o pedido do dono de ordenar o menu em
    // A→Z (grupos entre os grupos, pelo rótulo exibido) a substituiu. Se quiserem a posição fixa
    // de volta, é uma exceção pedida à parte (como o pin da Home em `ordenarMenu`), não um
    // retorno da ordem do array.
    //
    // Renderiza "Painéis": `$t(item.title)` e o `pt.json` do repo já traduzia
    // "Dashboards" antes deste módulo — por isso a ordenação usa o rótulo traduzido.
    title: 'Dashboards',
    icon: 'chart-square-line-duotone',
    BgColor: 'primary',
    to: '#',
    module: 'dashboards',
    children: [{ title: 'P&D tecnológico', to: '/dashboards/pd-tecnologico' }],
  },
  {
    // Assistente (Chat Corporativo · AI Workstation). Item ÚNICO, sem sub-itens:
    // o histórico de conversas e o "Nova conversa" ficam na coluna esquerda da
    // própria tela, como nos chats convencionais (ChatGPT/Gemini/Claude) — a
    // navegação entre conversas é da tela, não do menu do Hub.
    title: 'Assistente',
    icon: 'chat-round-dots-line-duotone',
    BgColor: 'primary',
    to: '/assistente',
    newUntil: '2026-08-29', // módulo novo — etiqueta "New" some sozinha ~30 dias após o lançamento
  },
  {
    // Licitações (radar de editais do PNCP, alimentado pelo Argos). O menu usa o
    // nome do NEGÓCIO, não o do serviço interno: quem lê a fila não precisa
    // saber o que é Argos. O painel de operação (`/licitacoes/operacao`, nível
    // `admin`) NÃO tem item de menu: a entrada é o botão "Operação" no
    // cabeçalho da fila, que só aparece para quem tem admin naquela rota.
    title: 'Licitações',
    icon: 'scale-line-duotone',
    BgColor: 'primary',
    to: '/licitacoes',
    module: 'licitacoes',
    newUntil: '2026-11-02', // etiqueta "New" some sozinha ~30 dias após a entrada em QA (2026-10-02)
  },
  {
    // Auditoria de Comodato (spec 2026-10-12): inventário em campo das remessas em comodato.
    // Rota de permissão única /auditoria-comodato; as telas herdam o nível.
    title: 'Auditoria de Comodato',
    icon: 'clipboard-check-line-duotone',
    BgColor: 'primary',
    to: '#',
    module: 'auditoria-comodato',
    newUntil: '2026-12-15',
    children: [
      { title: 'Painel', to: '/auditoria-comodato' },
      { title: 'Escolas', to: '/auditoria-comodato/escolas' },
      { title: 'Itens no app', to: '/auditoria-comodato/itens' },
      { title: 'Consolidado fiscal', to: '/auditoria-comodato/consolidado' },
      { title: 'Aparelhos', to: '/auditoria-comodato/aparelhos' },
    ],
  },
  {
    // Relatório Municipal Consolidado: dossiê de um município a partir de fontes
    // públicas (IBGE/TSE/INEP/Câmara). Tela única — a busca é dentro dela.
    title: 'Relatório Municipal',
    icon: 'city-line-duotone',
    BgColor: 'primary',
    to: '/relatorio-municipal',
    module: 'relatorio-municipal',
    newUntil: '2026-09-11', // etiqueta "New" some sozinha ~30 dias após o lançamento
  },
  {
    // Recursos Humanos: grupo-pai para o que o RH usa no dia a dia. Hoje só tem Controle de Acesso
    // (faciais); entra antes de Colaboradores. O grupo some sozinho quando o tenant não tem o
    // módulo ou o usuário não tem a rota (filterMenuByPermissions remove container sem filho).
    title: 'Recursos Humanos',
    icon: 'users-group-rounded-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        // Controle de Acesso (faciais): Cadastro · Agenda · Device · Log, cada uma uma página com
        // cabeçalho próprio, sem menu dentro da tela. Quem usa no dia a dia é o RH (fila
        // "aguardando RH"); o TI só mexe em Device. Permissão por seção, fechada por padrão (admin
        // em cada /seguranca/controle-acesso/<seção>): o item some para quem não tem a seção.
        // `module` também nos filhos: o filtro de módulo roda por item, e o grupo sozinho não basta.
        title: 'Controle de Acesso',
        icon: 'shield-user-line-duotone',
        BgColor: 'primary',
        to: '#',
        module: 'controle-acesso',
        newUntil: '2026-12-01',
        children: [
          { title: 'Cadastro', to: '/seguranca/controle-acesso/cadastro', module: 'controle-acesso' },
          { title: 'Agenda', to: '/seguranca/controle-acesso/agenda', module: 'controle-acesso' },
          { title: 'Device', to: '/seguranca/controle-acesso/device', module: 'controle-acesso' },
          { title: 'Log', to: '/seguranca/controle-acesso/log', module: 'controle-acesso' },
        ],
      },
    ],
  },
  {
    title: 'Colaboradores',
    icon: 'users-group-two-rounded-line-duotone',
    BgColor: 'primary',
    to: '/users',
  },
  {
    title: 'Portifólio',
    icon: 'box-line-duotone',
    BgColor: 'primary',
    to: '/produtos',
  },
  {
    title: 'Wiki',
    icon: 'book-2-line-duotone',
    BgColor: 'primary',
    to: '/wiki',
  },
  {
    // Módulo Inovação: colaboradores propõem ideias por centro de custo; o
    // diretor do CC (Identidade) avalia. "Ideias" (fila de avaliação) só
    // aparece para diretor/admin — flag dinâmica useInovacaoAvaliadorFlag.
    title: 'Inovação',
    icon: 'lightbulb-bolt-line-duotone',
    BgColor: 'primary',
    to: '#',
    newUntil: '2026-09-10', // módulo novo — etiqueta "New" some sozinha ~30 dias após o lançamento
    children: [
      { title: 'Nova Ideia', to: '/inovacao/nova' },
      { title: 'Minhas Ideias', to: '/inovacao/minhas' },
      { title: 'Ideias', to: '/inovacao/ideias', requiresAvaliadorInovacao: true },
    ],
  },
  {
    title: 'Ticket',
    icon: 'ticket-line-duotone',
    BgColor: 'primary',
    to: '/ticket',
    badgeKey: 'ticketNew',
    newUntil: '2026-07-31', // módulo novo — etiqueta "New" some sozinha após 30 dias (lançado ~01/07/2026)
    roleFlagAccess: 'isAgenteDesk', // agente de entidade sem nível em /ticket (ex.: prestador externo) vê o menu
    children: [
      { title: 'Chamados', to: '/ticket', roleFlagAccess: 'isAgenteDesk' },
      { title: 'Abrir chamado', to: '/ticket/novo', roleFlagAccess: 'isAgenteDesk' },
      // Redesign console (spec 2026-07-29) — etiqueta "New" some sozinha após 30 dias.
      { title: 'Relatórios', to: '/ticket/relatorios', newUntil: '2026-08-28' },
    ],
  },
  {
    title: 'Financeiro',
    icon: 'wallet-money-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        title: 'Reembolso',
        to: '/reembolso',
        children: [
          { title: 'Meu Reembolso', to: '/reembolso', badgeKey: 'reembolsoAprovacao' },
          { title: 'Central do financeiro', to: '/reembolso/financeiro', requiresCentralFinanceiro: true, badgeKey: 'reembolsoConferencia' },
          { title: 'Configurações', to: '/reembolso/configuracoes' },
          // Identidade Protheus ↔ Hub (spec 2026-10-15): aparece para quem tem concessão na rota.
          // Dados GLOBAIS (sem tenant_id) ⇒ só no tenant master.
          { title: 'Identidade', to: '/reembolso/identidade', masterOnly: true },
        ],
      },
      {
        title: 'Cartão de Crédito',
        to: '#',
        children: [
          { title: 'Faturas', to: '/cartao-corporativo/dashboard-analista' },
          { title: 'Minhas faturas', to: '/cartao-corporativo/gestor', requiresGestor: true },
        ],
      },
      {
        title: 'Gateway',
        to: '#',
        children: [
          { title: 'Novo Pagamento', to: '/gateway/novo-pagamento' },
          { title: 'Histórico Pagamentos', to: '/gateway/lista-pagamentos' },
          { title: 'Clientes', to: '/gateway/clientes' },
        ],
      },
      {
        title: 'Relatórios',
        to: '#',
        children: [
          { title: 'Extratos Bancários', to: '/financeiro/relatorios/extratos' },
          { title: 'Posição de Clientes', to: '/financeiro/relatorios/posicao-clientes', newUntil: '2026-08-15' },
          { title: 'Posição de Fornecedores', to: '/financeiro/relatorios/posicao-fornecedores', newUntil: '2026-08-15' },
          { title: 'Contas CC', to: '/financeiro/centro-de-custo' },
        ],
      },
      { title: 'Documentos Fiscais', to: '/financeiro/relatorios/fiscal-tss', newUntil: '2026-09-30' },
    ],
  },
  {
    title: 'Compras',
    icon: 'cart-large-2-line-duotone',
    BgColor: 'primary',
    to: '#',
    badgeKey: 'purchaseOrderNew',
    children: [
      {
        title: 'Liberações de Compras',
        to: '/pedidos-compra',
      },
      {
        title: 'Relatórios',
        to: '#',
        children: [
          {
            title: 'Relatório de Compras',
            to: '/compras/relatorios/pedidos',
            newUntil: '2026-09-11',
          },
        ],
      },
    ],
  },
  {
    title: 'Controladoria',
    icon: 'chart-2-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        title: 'Ativos Clientes',
        to: '/controladoria/ativos-clientes',
      },
    ],
  },
  {
    title: 'Logística',
    icon: 'delivery-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        title: 'Cotação de Fretes',
        to: '/cotacao-fretes'
      },
      // Gateway de Frete · transportadora. As telas ficam agrupadas para deixar
      // claro que cotação e acompanhamento são fluxos diferentes.
      //
      // ⚠️ A permissão é uma rota PRÓPRIA (`/cotacao-fretes/rastreio`), não a de
      // `/cotacao-fretes`: `utils/userRouteAccess.ts` casa path exato e não tem
      // herança de prefixo (só carve-outs nomeados — /assistente, /policies,
      // /wiki e os painéis via/sim), então o que não está no mapa é negado. A
      // migration 194 cria a rota e COPIA as concessões de `/cotacao-fretes`, o
      // que iguala os dois no deploy; daí para frente concessão nova precisa ser
      // dada nas rotas correspondentes (elas aparecem juntas na aba "Cotação de Fretes" do
      // /permissions).
      {
        title: 'Transportadora',
        to: '#',
        children: [
          { title: 'Remessas', to: '/logistica/transportadora/remessas' },
          { title: 'Rastreio de Frete', to: '/cotacao-fretes/rastreio' },
        ]
      },
      {
        title: 'Saldo Estoque',
        to: '/logistica/saldos'
      },
      {
        title: 'Executivo ACD',
        to: '/logistica/separacao'
      },
      {
        title: 'Relatórios',
        to: '#',
        children: [
          { title: 'Saldos por Endereço', to: '/logistica/relatorios/saldos-endereco', newUntil: '2026-08-15' },
        ],
      },
    ]
  },
  {
    title: 'Reuniões',
    icon: 'calendar-line-duotone',
    BgColor: 'primary',
    to: '/bookings',
    children: [
      {
        title: 'Reuniões',
        to: '/bookings'
      },
      {
        title: 'Agendar Reunião',
        to: '/bookings/new'
      },
      {
        title: 'Minhas Reuniões',
        to: '/bookings/my-bookings'
      }
    ]
  },
  {
    title: 'Plataformas',
    icon: 'layers-minimalistic-line-duotone',
    BgColor: 'primary',
    // Usamos '#' no item pai para ele apenas expandir o menu, sem tentar mudar de página
    to: '#',
    children: [
      {
        title: 'Astromaker',
        // A rota leva o parâmetro 'astromaker'
        to: '/plataformas/astromaker'
      },
      {
        title: 'Prodigius',
        // A rota leva o parâmetro 'prodigius'
        to: '/plataformas/prodigius'
      },
      {
        title: 'SIMRobótica',
        // A rota leva o parâmetro 'simrobotica'
        to: '/plataformas/simrobotica'
      },
      // {
      //   title: 'SIMRobótica +',
      //   // A rota leva o parâmetro 'simroboticaplus'
      //   to: '/plataformas/simroboticaplus'
      // },
      {
        title: 'Advento Tech',
        // A rota leva o parâmetro 'adventotech'
        to: '/plataformas/adventotech'
      }
    ]
  },
  {
    title: 'Marketing',
    icon: 'gallery-favourite-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        title: 'Merlin',
        icon: 'mdi-auto-fix',
        to: '/marketing/strategist'
      },
      {
        title: 'Coleção',
        icon: 'mdi-view-grid-outline',
        to: '/marketing/colecao'
      },
      {
        title: 'Criação',
        BgColor: 'primary',
        children: [
          {
            title: 'Criação Simples',
            to: '/marketing'
          },
          {
            title: 'Criação Planejada',
            to: '/marketing/planning'
          }
        ]
      },
      {
        title: 'Base de Dados',
        BgColor: 'primary',
        children: [
          {
            title: 'Brandings',
            to: '/marketing/brandings'
          },
          {
            title: 'Biblioteca de Mídia',
            to: '/marketing/media'
          },
          {
            title: 'Biblioteca de Skills',
            to: '/marketing/skills'
          }
        ]
      },
      {
        title: 'Meta Ads',
        BgColor: 'primary',
        children: [
          {
            title: 'Dashboard',
            to: '/marketing/ads-dashboard'
          },
          {
            title: 'Painel de Anúncios',
            to: '/marketing/ads-panel'
          },
          {
            title: 'Públicos Personalizados',
            to: '/marketing/audiences'
          },
          {
            title: 'Regras Automáticas',
            to: '/marketing/ads-rules'
          }
        ]
      }
    ]
  },
  {
    title: 'E-Learning',
    icon: 'mdi:school-outline',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        title: 'Todos os Cursos',
        to: '/courses'
      },
      {
        title: 'Meus Certificados',
        to: '@profile?tab=certificates',
        module: 'courses',
      }
    ]
  },
  {
    title: 'Governança',
    icon: 'document-text-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      { title: 'Dashboard Políticas', to: '/policies/dashboard' },
      { title: 'Documentos e Processos', to: '/policies' },
      { title: 'Administração', to: '/policies/admin' }
    ]
  },
  {
    title: 'E-commerce',
    icon: 'shop-2-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        title: 'Master Loja',
        to: '#',
        newUntil: '2026-08-14', // módulo novo — etiqueta "New" some sozinha ~30 dias após o lançamento
        children: [
          { title: 'Produtos', to: '/ecommerce/master-loja' },
          { title: 'Categorias', to: '/ecommerce/master-loja/categorias' },
          { title: 'Banners', to: '/ecommerce/master-loja/banners' },
          { title: 'Promoções', to: '/ecommerce/master-loja/promocoes' },
          { title: 'Campanhas', to: '/ecommerce/master-loja/campanhas' },
          { title: 'Pedidos', to: '/ecommerce/master-loja/pedidos' },
          { title: 'Expedição', to: '/ecommerce/master-loja/expedicao' },
          { title: 'Clientes', to: '/ecommerce/master-loja/clientes' },
          { title: 'Integração Protheus', to: '/ecommerce/master-loja/integracao-protheus' },
          { title: 'Logs e auditoria', to: '/ecommerce/master-loja/auditoria-protheus' },
          { title: 'Integração de catálogo', to: '/ecommerce/master-loja/integracao-catalogo' },
        ],
      },
      {
        title: 'Loja Escola',
        to: '#',
        children: [
          {
            title: 'Produtos',
            to: '/loja-escola/produtos'
          },
          {
            title: 'Pedidos',
            to: '/loja-escola/pedidos'
          },
          {
            title: 'Clientes',
            to: '/loja-escola/clientes'
          },
          {
            title: 'Métricas',
            to: '/loja-escola/metricas'
          },
          {
            title: 'Metafields',
            to: '/loja-escola/metafields'
          },
          {
            title: 'Logs',
            to: '/loja-escola/logs'
          },
          {
            title: 'Escolas',
            to: '/loja-escola/escolas'
          },
          {
            title: 'Vouchers',
            to: '/loja-escola/vouchers'
          }
        ]
      }
    ]
  },
  {
    title: 'Links Úteis',
    icon: 'link-round-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [] // Será preenchido dinamicamente pela API
  },
  {
    title: 'Tecnologia',
    icon: 'cpu-bolt-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      {
        title: 'ITAM',
        to: '#',
        children: [
          { title: 'Painel', to: '/itam/dashboard' },
          { title: 'Custos', to: '/itam/costs' },
          { title: 'Relatórios', to: '/itam/reports' },
          { title: 'Dispositivos', to: '/itam/devices' },
          { title: 'Domínios', to: '/itam/domains' },
          { title: 'Licenças', to: '/itam/licenses' },
          { title: 'Telefonia', to: '/itam/phone-lines' },
          // Faturas TI: contas recorrentes do TI até o financeiro (spec 2026-10-05). A rota
          // continua /tecnologia/faturas-ti (é o path das permissões); só o menu mora no ITAM.
          { title: 'Faturas', to: '/tecnologia/faturas-ti', module: 'faturas-ti', newUntil: '2026-12-01' },
        ],
      },
      {
        // Console de administração Entra ID / M365 / Intune. Master-only: o
        // backend garante com requireMasterTenant, e o item some nos tenants
        // filhos pelo gate de módulo ('m365' em enabled_modules).
        title: 'Microsoft 365',
        to: '#',
        newUntil: '2026-08-26',
        module: 'm365',
        masterOnly: true,
        children: [
          { title: 'Usuários', to: '/m365/usuarios' },
          { title: 'Grupos', to: '/m365/grupos' },
          { title: 'Segurança', to: '/m365/seguranca' },
          { title: 'Dispositivos', to: '/m365/dispositivos' },
          { title: 'Caixas compartilhadas', to: '/m365/caixas-compartilhadas' },
          { title: 'Listas de transmissão', to: '/m365/listas' },
          { title: 'Auditoria', to: '/m365/auditoria' },
          { title: 'Configurações', to: '/m365/configuracoes' },
        ],
      },
    ],
  },
  {
    title: 'PCO / Faturas',
    icon: 'bill-list-line-duotone',
    BgColor: 'primary',
    to: '#',
    children: [
      { title: 'Dashboard', to: '/resources/dashboard' },
      { title: 'Previsto × Consolidado', to: '/resources/plan-vs-actual' },
      { title: 'Orçamentos', to: '/resources/budgets' },
      { title: 'Contratos', to: '/resources/contracts' },
      { title: 'Faturas', to: '/resources/invoices' },
      { title: 'Rateio por departamento', to: '/resources/allocations' },
      { title: 'Calculadora de rateio', to: '/resources/allocation-calculator' },
      { title: 'Fechamentos', to: '/resources/closings' },
      { title: 'Relatórios', to: '/resources/reports' },
    ]
  },
  {
    title: 'Mídia indoor TV',
    icon: 'monitor-smartphone-line-duotone',
    BgColor: 'primary',
    to: '/indoor-tv/admin'
  },
  { header: 'Admin Master' },
  {
    title: 'Tenants',
    icon: 'buildings-2-line-duotone',
    BgColor: 'error',
    to: '/admin/tenants',
    masterOnly: true,
  },
  {
    title: 'API',
    icon: 'round-transfer-horizontal-line-duotone',
    BgColor: 'error',
    to: '/apis/gestao',
    newUntil: '2026-08-18',
  },
  {
    title: 'Destinos do switcher',
    icon: 'transfer-horizontal-line-duotone',
    BgColor: 'error',
    to: '/apis/destinos',
    newUntil: '2026-08-25',
  },
  {
    // Painel de métricas da AI Workstation (o serviço de IA por trás do chat).
    // Rota própria de nível `admin` (migration 182) — não é a permissão `member`
    // do módulo. `module` explícito porque o item deve sumir junto com o módulo
    // 'assistente' desligado no tenant.
    title: 'Assistente · Painel IA',
    icon: 'chart-square-line-duotone',
    BgColor: 'error',
    to: '/assistente/admin',
    module: 'assistente',
    newUntil: '2026-08-31',
  },
  {
    title: 'Reuniões',
    icon: 'calendar-line-duotone',
    BgColor: 'error',
    to: '#',
    children: [
      { title: 'Salas', to: '/bookings/admin/rooms' },
      { title: 'Reservas', to: '/bookings/all-bookings' },
    ],
  },
  {
    title: 'Permissões',
    icon: 'shield-user-line-duotone',
    BgColor: 'error',
    to: '#',
    children: [
      { title: 'Rotas', to: '/permissions', exact: true },
      { title: 'Usuários', to: '/permissions/users' },
      { title: 'Grupos', to: '/permissions/custom-groups' },
    ],
  },
  {
    title: 'Tickets',
    icon: 'ticket-line-duotone',
    BgColor: 'error',
    to: '#',
    children: [
      { title: 'Admin · Entidades', to: '/ticket/admin/entidades' },
      { title: 'Admin · SLA', to: '/ticket/admin/sla' },
      { title: 'Admin · Formulários', to: '/ticket/admin/forms' },
      { title: 'Admin · Parâmetros', to: '/ticket/admin/parametros' },
    ],
  },
  {
    title: 'Cursos',
    icon: 'book-2-line-duotone',
    BgColor: 'error',
    to: '/courses/admin/courses',
  },
  {
    title: 'Logs',
    icon: 'document-text-line-duotone',
    BgColor: 'error',
    to: '/logs',
  }
];

/**
 * Ordena o menu em TODOS os níveis: itens SEM filhos primeiro (A→Z), depois os grupos (A→Z).
 * A→Z em pt-BR, sem distinguir caixa nem acento. Itens `header` (legendas de seção) não são
 * atravessados: cada trecho entre headers é ordenado sozinho e o header fica onde está. Título
 * vazio vai por último, na ordem original. `fixarHome` (só no nível de cima) prende o "Home" no
 * começo do primeiro trecho. Função pura: devolve arrays novos e nunca altera a entrada; o
 * `Array.sort` é estável, então títulos iguais mantêm a ordem de origem.
 */
export function ordenarMenu(items: MenuItem[], opts: { fixarHome?: boolean; rotulo?: (it: MenuItem) => string } = {}): MenuItem[] {
  const ehGrupo = (it: MenuItem) => Array.isArray(it.children);
  const cmp = (a: MenuItem, b: MenuItem) => {
    if (opts.fixarHome) {
      const ha = a.title === 'Home';
      const hb = b.title === 'Home';
      if (ha !== hb) return ha ? -1 : 1;
    }
    const ga = ehGrupo(a);
    const gb = ehGrupo(b);
    if (ga !== gb) return ga ? 1 : -1;
    // `rotulo` = o texto que a sidebar EXIBE ($t(title)): ordenar pelo título cru deixaria
    // "Dashboards" em D lendo "Painéis". Sem resolvedor, vale o título.
    const ta = (a.title ? (opts.rotulo ? opts.rotulo(a) : a.title) : '') ?? '';
    const tb = (b.title ? (opts.rotulo ? opts.rotulo(b) : b.title) : '') ?? '';
    if (!ta || !tb) return ta ? -1 : tb ? 1 : 0;
    return ta.localeCompare(tb, 'pt-BR', { sensitivity: 'base' });
  };
  const recursivo = (it: MenuItem): MenuItem => (it.children ? { ...it, children: ordenarMenu(it.children, { rotulo: opts.rotulo }) } : { ...it });
  const out: MenuItem[] = [];
  let trecho: MenuItem[] = [];
  const fecha = () => {
    out.push(...trecho.map(recursivo).sort(cmp));
    trecho = [];
  };
  for (const it of items) {
    if (it.header) {
      fecha();
      out.push({ ...it });
    } else {
      trecho.push(it);
    }
  }
  fecha();
  return out;
}

/**
 * Menu já ordenado pelo TÍTULO (base estável: atalhos da Home, testes). As três sidebars
 * reordenam pelo RÓTULO traduzido, depois de acoplar os links dinâmicos (Links Úteis), com
 * `ordenarMenuExibido` — o idioma muda em tempo de execução e a API entrega links depois.
 */
export const menuItems: MenuItem[] = ordenarMenu(menuItemsBruto, { fixarHome: true });

/** Ordenação final de exibição: mesma regra, pelo rótulo que a sidebar mostra (`t(title)`). */
export function ordenarMenuExibido(items: MenuItem[], traduzir: (titulo: string) => string): MenuItem[] {
  return ordenarMenu(items, { fixarHome: true, rotulo: (it) => traduzir(it.title ?? '') });
}

// Função reutilizável para verificar permissões
export function hasPermission(
  item: MenuItem,
  isAdminMaster: boolean,
  userGroups: string[] = [],
  userDepartment: string = '',
  userCompany: string = '',
  userPermissions?: {
    hasAdminAccess: boolean;
    hasEditorAccess: boolean;
    isAdmin: boolean;
    isEditor: boolean;
  },
  adminPermissions?: Map<string, 'admin' | 'editor'>,
  explicitAccessPaths?: string[] // Paths com acesso via user_route_level (member/editor/admin)
): boolean {
  // Se é um header, sempre mostrar (será filtrado depois se a seção não tiver items visíveis)
  if (item.header) {
    return true;
  }

  // Se não tem path/to, sempre mostrar (item genérico)
  if (!item.to) {
    return true;
  }

  // Se é um link externo, sempre permitir (não é rota do sistema)
  if (item.type === 'external' || item.to.startsWith('http://') || item.to.startsWith('https://')) {
    return true;
  }

  // Multi-tenant (US4): esconder itens de MÓDULO desligado para o tenant atual.
  // Aplica-se inclusive ao admin master — mas na Atlan todos os módulos estão
  // ativos, então nada some lá. Só filtra após o branding do tenant carregar
  // (evita esconder tudo durante o bootstrap). CORE (sem slug) nunca é filtrado.
  const moduleSlug = item.module || (item.to && item.to !== '#' ? moduleSlugForPath(item.to) : null);
  if (moduleSlug) {
    try {
      const tenantStore = useTenantStore();
      if (tenantStore.loaded && !tenantStore.enabledModules.includes(moduleSlug)) {
        return false;
      }
    } catch {
      /* store indisponível em contextos raros */
    }
  }

  // Multi-tenant: itens exclusivos do tenant master (administração de tenants) só
  // aparecem no domínio da Atlan. O backend também barra (requireMasterTenant/404).
  if (item.masterOnly) {
    try {
      const tenantStore = useTenantStore();
      if (!tenantStore.isMaster) {
        return false;
      }
    } catch {
      // store indisponível → oculta por segurança (nunca vaza admin de outro domínio)
      return false;
    }
  }

  // userAccessMap (GET /me): nível efetivo por path — alinha menu ao Vue Router guard
  try {
    if (item.to && item.to !== '#') {
      const navPathRaw = normalizeRoutePath(item.to);
      if (navPathRaw.startsWith('/')) {
        const authStore = useAuthStore();
        const aclMap = authStore.userAccessMap;

        // Falha do /me ou bootstrap incomum: mapa vazio — não mostrar URLs internas a não-Admins (inclui /permissions; evita menu enganoso)
        if (!isAdminMaster && aclMap.size === 0) {
          return false;
        }

        if (aclMap.size > 0) {
          const effective = resolveUserRouteAccessLevel(navPathRaw, aclMap);

          if (isAdminMaster) {
            if (effective === 'none' && !isPermissionsConfigurationPath(navPathRaw)) {
              return false;
            }
          } else {
            // /me é fonte de verdade — fail-secure como o router guard (inclui path ausente no mapa)
            if (effective === undefined || effective === 'none') {
              // Resgate por papel dinâmico (roleFlags do /me): ex.: agente do Desk sem
              // nível em /ticket (prestador externo). O backend valida por cobertura.
              if (item.roleFlagAccess && authStore.roleFlags?.[item.roleFlagAccess] === true) {
                return true;
              }
              return false;
            }
            return true;
          }
        }
      }
    }
  } catch {
    /* store indisponível em contextos raros */
  }

  // Admin Master: já filtramos `effective === none` acima para rotas não-`/permissions` (e apenas Master bypassa permissões inactive)
  if (isAdminMaster) {
    return true;
  }

  // Se é um container (#), verificar se tem children (será filtrado depois)
  if (item.to === '#') {
    // Containers são permitidos, mas serão filtrados baseados nos children
    return true;
  }

  // Fallback legado apenas se userAccessMap ainda não carregou (bootstrap / erro de rede)
  const hasAccess = hasMenuAccess(item.to, userGroups, isAdminMaster, userPermissions, explicitAccessPaths);
  if (!hasAccess) {
    return false;
  }

  // ⚠️ Verificação de permissões legadas (isAllowed) - apenas como fallback
  // Estas permissões devem ser migradas para o banco de dados
  if (item.isAllowed) {
    if (Array.isArray(item.isAllowed) && item.isAllowed.length > 0) {
      const hasGroupAccess = item.isAllowed.some((allowed: string) =>
        userGroups.some((group: string) => group === allowed)
      );
      const hasDepartmentAccess = item.isAllowed.includes(userDepartment);
      const hasCompanyAccess = item.isAllowed.includes(userCompany);

      return hasGroupAccess || hasDepartmentAccess || hasCompanyAccess;
    }
  }

  // Por padrão, permitir se passou todas as verificações acima
  return true;
}

// Função para verificar se uma rota corresponde a um item de menu
function isRouteActive(item: MenuItem, currentPath: string): boolean {
  // Se é header ou não tem to, não é ativo
  if (item.header || !item.to) {
    return false;
  }

  // Links externos nunca são ativos (não são rotas do sistema)
  if (item.type === 'external' || item.to.startsWith('http://') || item.to.startsWith('https://')) {
    return false;
  }

  // Se o to é '#', verificar apenas os children
  if (item.to === '#') {
    return false;
  }

  // Normalizar paths (remover trailing slash e query params)
  const normalizePath = (path: string) => {
    return path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  };

  // Extrair query params
  const getQueryParams = (path: string): Record<string, string> => {
    const queryString = path.includes('?') ? path.split('?')[1].split('#')[0] : '';
    if (!queryString) return {};

    const params: Record<string, string> = {};
    queryString.split('&').forEach(param => {
      const [key, value] = param.split('=');
      if (key) params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });
    return params;
  };

  const normalizedItemPath = normalizePath(item.to);
  const normalizedCurrentPath = normalizePath(currentPath);

  // Extrair query params do item do menu e da rota atual
  const itemQueryParams = getQueryParams(item.to);
  const currentQueryParams = getQueryParams(currentPath);

  // Se a rota atual tem query params 'tab', aplicar regras MUITO rigorosas
  if (currentQueryParams.tab) {
    // Se o item também tem query params 'tab', verificar se corresponde EXATAMENTE
    if (itemQueryParams.tab) {
      // Path deve corresponder EXATAMENTE - não permitir sub-rotas quando há query params 'tab'
      // Ex: /users/{samaccountname} deve corresponder apenas a /users/{samaccountname}, não a /users
      if (normalizedItemPath !== normalizedCurrentPath) {
        return false;
      }

      // O tab DEVE ser exatamente o mesmo
      if (itemQueryParams.tab !== currentQueryParams.tab) {
        return false;
      }

      // Se chegou aqui, path e tab correspondem exatamente
      return true;
    } else {
      // Item NÃO tem query params 'tab', mas a rota atual TEM → não deve ser ativo
      // Isso evita que "Colaboradores" ou "Cursos" fiquem ativos quando está em ?tab=notes ou ?tab=kanban
      // EXCEÇÃO: Se o item tem children, não marcar como ativo automaticamente
      // (os children serão verificados separadamente)
      return false;
    }
  }

  // Se a rota atual NÃO tem 'tab', mas o item TEM 'tab', não deve ser ativo
  // (ex: se estiver em /users sem tab, Kanban/Notes não devem estar ativos)
  if (itemQueryParams.tab && !currentQueryParams.tab) {
    return false;
  }

  // Se o item tem outros query params (não 'tab'), verificar normalmente
  if (Object.keys(itemQueryParams).length > 0) {
    const pathMatches = normalizedItemPath === normalizedCurrentPath ||
      (!item.exact && normalizedItemPath !== '/' && normalizedCurrentPath.startsWith(normalizedItemPath + '/'));

    if (!pathMatches) {
      return false;
    }

    // Verificar se todos os query params do item estão presentes e com os mesmos valores
    for (const [key, value] of Object.entries(itemQueryParams)) {
      if (currentQueryParams[key] !== value) {
        return false;
      }
    }
    return true;
  }

  // Item sem query params - verificar correspondência exata do path
  // Ex: /users só deve ser ativo se a rota for exatamente /users
  if (normalizedItemPath === normalizedCurrentPath) {
    return true;
  }

  // Itens com match exato não devem herdar active de sub-rotas (ex: /permissions vs /permissions/users)
  if (item.exact) {
    return false;
  }

  // Para outros casos (como /bookings que pode ter sub-rotas), permitir correspondência parcial
  // Mas NUNCA quando a rota atual tem query params (especialmente 'tab')
  // Isso evita que /users fique ativo quando está em /users/{samaccountname}?tab=notes
  if (Object.keys(currentQueryParams).length > 0) {
    return false;
  }

  // Apenas permitir correspondência parcial se não houver query params
  if (normalizedItemPath !== '/' &&
    normalizedCurrentPath.startsWith(normalizedItemPath + '/')) {
    return true;
  }

  return false;
}

// Função para marcar itens de menu como ativos baseado na rota atual
export function markMenuItemsActive(
  items: MenuItem[],
  currentPath: string
): MenuItem[] {
  return items.map(item => {
    // Se é header, não marcar como ativo
    if (item.header) {
      return item;
    }

    // Verificar se o item atual está ativo
    const isItemActive = isRouteActive(item, currentPath);

    // Se tem children, verificar recursivamente
    if (item.children && item.children.length > 0) {
      const markedChildren = markMenuItemsActive(item.children, currentPath);

      // Verificar se algum child está ativo
      const hasActiveChild = markedChildren.some(child => child.active === true);

      // Se a rota atual tem query params 'tab', ser MUITO rigoroso:
      // Não marcar o pai como ativo se apenas um child está ativo mas o tab não corresponde
      // Extrair query params usando a mesma lógica da função isRouteActive
      const getQueryParams = (path: string): Record<string, string> => {
        const queryString = path.includes('?') ? path.split('?')[1].split('#')[0] : '';
        if (!queryString) return {};
        const params: Record<string, string> = {};
        queryString.split('&').forEach(param => {
          const [key, value] = param.split('=');
          if (key) params[decodeURIComponent(key)] = decodeURIComponent(value || '');
        });
        return params;
      };

      const currentQueryParams = getQueryParams(currentPath);

      // Se há query param 'tab' na rota atual, ser MUITO rigoroso:
      // - Se o item pai tem to: '#', não marcar como ativo a menos que algum child tenha o tab exato
      // - Se algum child está ativo mas tem tab diferente, não marcar o pai como ativo
      if (currentQueryParams.tab) {
        if (item.to === '#') {
          // Item pai é um container (to: '#') - verificar se algum child tem o tab exato
          const hasMatchingTabChild = markedChildren.some(child => {
            if (!child.active || !child.to) return false;
            const childQueryParams = getQueryParams(child.to);
            return childQueryParams.tab === currentQueryParams.tab;
          });

          // Se nenhum child tem o tab exato, não marcar o pai como ativo
          if (!hasMatchingTabChild) {
            return {
              ...item,
              active: false,
              children: markedChildren
            };
          }
        } else if (hasActiveChild) {
          // Item pai tem um path real - verificar se o child ativo tem o mesmo tab
          const activeChild = markedChildren.find(child => child.active === true);
          if (activeChild && activeChild.to) {
            const childQueryParams = getQueryParams(activeChild.to);

            // Se o child ativo tem um tab diferente, não marcar o pai como ativo
            if (childQueryParams.tab && childQueryParams.tab !== currentQueryParams.tab) {
              return {
                ...item,
                active: false, // Não marcar pai como ativo se o tab não corresponde
                children: markedChildren
              };
            }
          }
        }
      }

      return {
        ...item,
        active: isItemActive || hasActiveChild,
        children: markedChildren
      };
    }

    // Item sem children
    return {
      ...item,
      active: isItemActive
    };
  });
}

// Função para processar links dinâmicos do menu (ex: @profile?tab=kanban)
export function processMenuLinks(
  items: MenuItem[],
  samaccountname?: string | null
): MenuItem[] {
  if (!samaccountname) {
    return items;
  }

  return items.map(item => {
    // Processar link do item principal
    let processedTo = item.to;
    if (processedTo?.startsWith('@profile')) {
      // Substituir @profile pelo link do perfil do usuário
      const queryParams = processedTo.includes('?') ? processedTo.split('?')[1] : '';
      processedTo = `/users/${samaccountname}${queryParams ? `?${queryParams}` : ''}`;
    }

    // Processar children recursivamente
    let processedChildren = item.children;
    if (item.children && item.children.length > 0) {
      processedChildren = processMenuLinks(item.children, samaccountname);
    }

    return {
      ...item,
      to: processedTo,
      children: processedChildren
    };
  });
}

// Função para filtrar menu baseado em permissões
export function filterMenuByPermissions(
  items: MenuItem[],
  isAdminMaster: boolean,
  userGroups: string[] = [],
  userDepartment: string = '',
  userCompany: string = '',
  userPermissions?: {
    hasAdminAccess: boolean;
    hasEditorAccess: boolean;
    isAdmin: boolean;
    isEditor: boolean;
  },
  adminPermissions?: Map<string, 'admin' | 'editor'>,
  explicitAccessPaths?: string[] // Paths com acesso via user_route_level (member/editor/admin)
): MenuItem[] {
  // Primeiro passo: filtrar itens baseado em permissões
  const filteredItems = items
    .map((item) => {
      // Se é header, manter temporariamente (será filtrado depois)
      if (item.header) {
        return item;
      }

      // ✅ NOVO: Tratamento especial para menu "Administração"
      if (item.title === 'Administração' && item.children) {
        // Se é admin master, mostrar tudo
        if (isAdminMaster) {
          return item;
        }

        // Filtrar children do menu Administração baseado em permissões específicas
        const filteredChildren = item.children
          .map(child => {
            // Se child tem sub-children (ex: Cursos, Reservas, Rotas)
            if (child.children) {
              // Filtrar sub-children baseado em permissões específicas
              const filteredSubChildren = child.children.filter(subChild =>
                hasPermission(subChild, isAdminMaster, userGroups, userDepartment, userCompany, userPermissions, adminPermissions, explicitAccessPaths)
              );

              // Se não tem nenhum sub-child com acesso, não mostrar este child
              if (filteredSubChildren.length === 0) {
                return null;
              }

              // Retornar child com sub-children filtrados
              return { ...child, children: filteredSubChildren };
            }

            // Child sem sub-children - verificar permissão normal
            if (hasPermission(child, isAdminMaster, userGroups, userDepartment, userCompany, userPermissions, adminPermissions, explicitAccessPaths)) {
              return child;
            }

            return null;
          })
          .filter((child): child is MenuItem => child !== null);

        // Se não tem nenhum child com acesso, não mostrar menu Administração
        if (filteredChildren.length === 0) {
          return null;
        }

        // Retornar menu Administração com children filtrados
        return { ...item, children: filteredChildren };
      }

      // Verificar permissão do item principal (passando adminPermissions)
      const hasMainAccess = hasPermission(item, isAdminMaster, userGroups, userDepartment, userCompany, userPermissions, adminPermissions, explicitAccessPaths);

      // Se tem children, verificar se pelo menos um tem acesso
      if (item.children && item.children.length > 0) {
        const filteredChildren = item.children.filter(child => {
          // Sub-container (sem `to` real ou com to:'#' mas com children):
          // manter apenas se ao menos um filho seu for acessível
          if (child.children && child.children.length > 0 && (!child.to || child.to === '#')) {
            return child.children.some(subChild =>
              hasPermission(subChild, isAdminMaster, userGroups, userDepartment, userCompany, userPermissions, adminPermissions, explicitAccessPaths)
            );
          }
          return hasPermission(child, isAdminMaster, userGroups, userDepartment, userCompany, userPermissions, adminPermissions, explicitAccessPaths);
        });

        // Se nenhum child tem acesso, esconder o item inteiro
        if (filteredChildren.length === 0) {
          return null;
        }

        // Retornar item com children filtrados
        return { ...item, children: filteredChildren };
      }

      // Container sem children visíveis — esconder (evita menu expandível vazio)
      if (item.to === '#') return null;

      // Se não tem children, verificar apenas a permissão principal
      return hasMainAccess ? item : null;
    })
    .filter((item): item is MenuItem => item !== null)
    .map(item => {
      // Filtrar children recursivamente (se ainda houver)
      if (item.children && item.children.length > 0 && item.title !== 'Administração') {
        return {
          ...item,
          children: filterMenuByPermissions(
            item.children,
            isAdminMaster,
            userGroups,
            userDepartment,
            userCompany,
            userPermissions,
            adminPermissions,
            explicitAccessPaths
          )
        };
      }
      return item;
    });

  // Segundo passo: remover headers órfãos (headers sem itens visíveis depois)
  // Um header é órfão se:
  // 1. É seguido imediatamente por outro header
  // 2. É o último item do array
  // 3. Não tem nenhum item (não-header) até o próximo header
  const finalItems: MenuItem[] = [];

  for (let i = 0; i < filteredItems.length; i++) {
    const currentItem = filteredItems[i];

    if (currentItem.header) {
      // Verificar se existe pelo menos um item não-header antes do próximo header
      let hasVisibleItems = false;

      for (let j = i + 1; j < filteredItems.length; j++) {
        const nextItem = filteredItems[j];

        // Se encontrou outro header, parar de procurar
        if (nextItem.header) {
          break;
        }

        // Encontrou um item não-header, então o header atual tem itens
        hasVisibleItems = true;
        break;
      }

      // Só adicionar o header se tiver itens visíveis depois dele
      if (hasVisibleItems) {
        finalItems.push(currentItem);
      }
    } else {
      // Não é header, adicionar normalmente
      finalItems.push(currentItem);
    }
  }

  return finalItems;
}
